import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Checkbox, Input, Modal, Select, Space, Typography } from 'antd'
import {
  errorNotification,
  successNotification,
  trackingShipmentActionUrl
} from '../../config'
import { postWithIdempotencyKey } from './api'
import {
  KIND_LABELS,
  buildRetrackPayload,
  classifyNumber,
  errorSentence,
  formatDateTime,
  isNoResponse,
  keyForSubmission,
  lineTracks,
  newIdempotencyKey,
  normalizeNumber,
  retrackWarnings,
  validateRetrack
} from './utils'

// The one dialog in the dashboard that spends the owner's money: a re-track buys a fresh ShipsGo
// shipment, about two dollars of credit, and ShipsGo does not give it back. So the cost is stated
// before the button is reachable, the button is dead until it is acknowledged, and every request
// carries the key made when the dialog opened — a lost response is then retried for free instead of
// being guessed at.
const RetrackModal = ({ visible, shipment, lines, account, onClose, onDone }) => {
  const row = shipment || {}
  const [number, setNumber] = useState('')
  const [lineId, setLineId] = useState(undefined)
  const [note, setNote] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [unconfirmed, setUnconfirmed] = useState(false)
  const [saving, setSaving] = useState(false)
  // One key per dialog opening. `keyForSubmission` hands it to the first submission and keeps it
  // for every retry of the same number, line and note.
  const [keyState, setKeyState] = useState({ key: null, signature: null })

  useEffect(() => {
    if (!visible) return
    setNumber(row.number || '')
    setLineId(row.lineId || undefined)
    setNote('')
    setAcknowledged(false)
    setSubmitted(false)
    setUnconfirmed(false)
    setSaving(false)
    setKeyState({ key: newIdempotencyKey(), signature: null })
    // eslint-disable-next-line
  }, [visible])

  const normalized = normalizeNumber(number)
  const kind = classifyNumber(normalized)
  const kindLabel =
    KIND_LABELS[kind] ||
    (kind === 'check_digit_failed' ? 'fails the check digit' : 'not a valid number')

  const errors = submitted
    ? validateRetrack({ shipment, number, lineId, note, lines, acknowledged })
    : []

  // Sorted the way the customer's picker is, so the team is choosing from the same list.
  const sorted = (lines || [])
    .slice()
    .sort((a, b) => Number((a || {}).position || 0) - Number((b || {}).position || 0))

  const optionFor = line => {
    const label = `${line.name}${line.scac ? ` (${line.scac})` : ''}`
    let reason = ''
    if (!line.active) reason = 'switched off'
    else if ((kind === 'container' || kind === 'reference') && !lineTracks(line, kind)) {
      reason = `does not track ${KIND_LABELS[kind]} numbers`
    }
    return { label: reason ? `${label} — ${reason}` : label, disabled: !!reason }
  }

  const credits = account ? account.creditsRemaining : null
  const creditsText =
    credits === null || credits === undefined
      ? 'Credits last seen: unknown.'
      : `Credits last seen: ${credits}${
          account && account.creditsSeenAt ? ` (${formatDateTime(account.creditsSeenAt)})` : ''
        }.`
  const warnings = retrackWarnings({ credits })

  const submit = async () => {
    // One request per click, whatever the mouse did: the button is disabled while in flight, and
    // this guard covers the double-click that lands before React re-renders it.
    if (saving) return
    setSubmitted(true)
    const found = validateRetrack({ shipment, number, lineId, note, lines, acknowledged })
    if (found.length) return

    setSaving(true)
    const payload = buildRetrackPayload({ number, lineId, note })
    const next = keyForSubmission(keyState, payload)
    setKeyState(next)
    const response = await postWithIdempotencyKey(
      trackingShipmentActionUrl(row.id, 'retrack'),
      payload,
      next.key
    )
    setSaving(false)

    if (response.success) {
      successNotification('Re-track accepted — starting.')
      onDone({ started: true })
      return
    }
    if (isNoResponse(response)) {
      // The request may well have been carried out. Staying open with the same key is what makes
      // pressing again safe: the server replays its first answer rather than buying a second one.
      setUnconfirmed(true)
      return
    }
    setUnconfirmed(false)
    errorNotification(errorSentence(response, 'The re-track was refused.'))
  }

  return (
    <Modal
      visible={visible}
      title={`Re-track ${row.number || ''}`.trim()}
      onCancel={onClose}
      onOk={submit}
      okText={unconfirmed ? 'Try again' : 'Spend 1 credit and re-track'}
      okButtonProps={{ danger: true, disabled: !acknowledged || saving }}
      confirmLoading={saving}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="This spends one ShipsGo credit. It cannot be undone."
          description={
            <div>
              <div>{`${creditsText} The old ShipsGo shipment stays in the action log.`}</div>
              {warnings.map(warning => (
                <div key={warning}>{warning}</div>
              ))}
            </div>
          }
        />

        {unconfirmed ? (
          <Alert
            type="warning"
            showIcon
            message="We could not confirm whether the re-track went through. Trying again with the same details is safe — it will not spend a second credit."
          />
        ) : null}

        <div>
          <div>Number</div>
          <Input value={number} onChange={event => setNumber(event.target.value)} />
          <div style={{ opacity: 0.65 }}>
            {normalized ? `Will send: ${normalized} · ${kindLabel}` : 'Enter the number'}
          </div>
        </div>

        <div>
          <div>Line</div>
          <Select
            value={lineId}
            onChange={setLineId}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            placeholder="Choose the shipping line"
            options={sorted.map(line => ({ ...optionFor(line), value: line.id }))}
          />
        </div>

        <div>
          <div>Note</div>
          <Input.TextArea
            rows={3}
            maxLength={1000}
            showCount
            value={note}
            onChange={event => setNote(event.target.value)}
          />
        </div>

        <Checkbox
          checked={acknowledged}
          onChange={event => setAcknowledged(event.target.checked)}
        >
          I understand this spends one ShipsGo credit
        </Checkbox>

        {errors.length ? (
          <div>
            {errors.map(message => (
              <div key={message}>
                <Typography.Text type="danger">{message}</Typography.Text>
              </div>
            ))}
          </div>
        ) : null}
      </Space>
    </Modal>
  )
}

RetrackModal.propTypes = {
  visible: PropTypes.bool,
  shipment: PropTypes.object,
  lines: PropTypes.array,
  account: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func
}

export default RetrackModal
